import { localizeEgg, localizeWeapon } from "../content-i18n";
import { EGGS, EGG_BY_ID, WEAPONS, WEAPON_BY_ID } from "../core/content";
import type { PlayerProfile } from "../core/profile";
import type { WeaponDefinition } from "../core/types";
import { spriteMarkup } from "./assets";
import {
  isLocale,
  languageOptions,
  type Locale,
} from "../i18n";

export type RunSetupStep = "egg" | "weapon" | "danger";

export interface RunSetupSelection {
  eggId: string;
  weaponId: string;
  dangerLevel: 0 | 1 | 2 | 3 | 4 | 5;
  endless: boolean;
}

interface RunSetupOptions {
  locale: Locale;
  profile: PlayerProfile;
  onComplete: (selection: RunSetupSelection) => void;
  onCancel: () => void;
  onLocaleChange?: (locale: Locale) => void;
}

interface DraftSelection {
  step: RunSetupStep;
  eggId: string;
  weaponId: string;
  dangerLevel: 0 | 1 | 2 | 3 | 4 | 5;
  endless: boolean;
}

export function eligibleStartingWeapons(
  unlockedWeaponIds: readonly string[],
  eggId: string,
): readonly WeaponDefinition[] {
  const egg = EGG_BY_ID.get(eggId) ?? EGGS[0];
  if (!egg) return [];
  const unlocked = new Set(unlockedWeaponIds);
  const choices = WEAPONS.filter((weapon) => unlocked.has(weapon.id));
  if (choices.length > 0) return choices;
  const fallback = WEAPON_BY_ID.get(egg.startingWeaponId);
  return fallback ? [fallback] : [];
}

export function mountRunSetup(root: HTMLElement, options: RunSetupOptions): void {
  const firstEggId =
    EGGS.find((egg) => options.profile.unlockedEggIds.includes(egg.id))?.id ??
    EGGS[0]?.id;
  if (!firstEggId) throw new Error("Eggato requires at least one egg character.");
  const firstWeapon = eligibleStartingWeapons(
    options.profile.unlockedWeaponIds,
    firstEggId,
  )[0];
  if (!firstWeapon) throw new Error("Eggato requires at least one unlocked weapon.");

  const draft: DraftSelection = {
    step: "egg",
    eggId: firstEggId,
    weaponId: firstWeapon.id,
    dangerLevel: 0,
    endless: false,
  };

  const render = (): void => {
    root.innerHTML = runSetupMarkup(options.locale, options.profile, draft);
    root
      .querySelector<HTMLSelectElement>("[data-language]")
      ?.addEventListener("change", (event) => {
        const locale = (event.target as HTMLSelectElement).value;
        if (isLocale(locale)) options.onLocaleChange?.(locale);
      });
    root.querySelector("[data-setup-cancel]")?.addEventListener("click", () => {
      options.onCancel();
    });
    root.querySelector("[data-setup-back]")?.addEventListener("click", () => {
      draft.step = draft.step === "danger" ? "weapon" : "egg";
      render();
    });
    root.querySelector("[data-egg-grid]")?.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest<HTMLElement>("[data-setup-egg]");
      const eggId = button?.dataset.setupEgg;
      if (!eggId || !options.profile.unlockedEggIds.includes(eggId)) return;
      draft.eggId = eggId;
      const eligible = eligibleStartingWeapons(
        options.profile.unlockedWeaponIds,
        eggId,
      );
      if (!eligible.some((weapon) => weapon.id === draft.weaponId)) {
        draft.weaponId = eligible[0]?.id ?? "";
      }
      draft.step = "weapon";
      render();
    });
    root.querySelector("[data-weapon-grid]")?.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest<HTMLElement>("[data-setup-weapon]");
      const weaponId = button?.dataset.setupWeapon;
      if (!weaponId) return;
      const eligible = eligibleStartingWeapons(
        options.profile.unlockedWeaponIds,
        draft.eggId,
      );
      if (!eligible.some((weapon) => weapon.id === weaponId)) return;
      draft.weaponId = weaponId;
      draft.step = "danger";
      render();
    });
    root.querySelector("[data-danger-grid]")?.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const button = target.closest<HTMLElement>("[data-setup-danger]");
      const value = Number(button?.dataset.setupDanger);
      if (!Number.isInteger(value) || value < 0 || value > 5) return;
      if (value > options.profile.unlockedDangerLevel) return;
      draft.dangerLevel = value as DraftSelection["dangerLevel"];
      render();
    });
    root
      .querySelector<HTMLInputElement>("[data-setup-endless]")
      ?.addEventListener("change", (event) => {
        draft.endless = (event.target as HTMLInputElement).checked;
      });
    root.querySelector("[data-setup-start]")?.addEventListener("click", () => {
      options.onComplete({
        eggId: draft.eggId,
        weaponId: draft.weaponId,
        dangerLevel: draft.dangerLevel,
        endless: draft.endless,
      });
    });
  };

  render();
}

function runSetupMarkup(
  locale: Locale,
  profile: PlayerProfile,
  draft: DraftSelection,
): string {
  const egg = EGG_BY_ID.get(draft.eggId) ?? EGGS[0];
  const weapon = WEAPON_BY_ID.get(draft.weaponId);
  if (!egg || !weapon) return "";
  const localizedEgg = localizeEgg(locale, egg);
  const localizedWeapon = localizeWeapon(locale, weapon);
  const stepNumber = draft.step === "egg" ? 1 : draft.step === "weapon" ? 2 : 3;
  return `
    <main class="run-setup-shell" data-setup-step="${draft.step}">
      <header class="setup-topbar">
        <button class="setup-ghost-button" type="button" data-setup-cancel>${copy(locale, "Back to title", "返回主菜单")}</button>
        <ol class="setup-progress" aria-label="${copy(locale, "Run setup progress", "开局选择进度")}">
          ${[1, 2, 3].map((step) => `<li class="${step === stepNumber ? "is-current" : step < stepNumber ? "is-complete" : ""}"><i>0${step}</i><span>${[copy(locale, "Egg", "角色"), copy(locale, "Weapon", "武器"), copy(locale, "Danger", "危险度")][step - 1]}</span></li>`).join("")}
        </ol>
        <label class="language-picker setup-language-picker">
          <span>${copy(locale, "Language", "语言")}</span>
          <select data-language aria-label="${copy(locale, "Language", "语言")}">${languageOptions(locale)}</select>
        </label>
      </header>
      <section class="setup-workspace">
        <aside class="setup-summary">
          ${spriteMarkup("egg", egg.id, "setup-egg-portrait")}
          <p class="setup-kicker">${localizedEgg.archetype}</p>
          <h1>${localizedEgg.name}</h1>
          <p>${localizedEgg.description}</p>
          <dl>
            <div><dt>${copy(locale, "Starting weapon", "初始武器")}</dt><dd>${localizedWeapon.name}</dd></div>
            <div><dt>${copy(locale, "Highest danger", "最高通关危险")}</dt><dd>D${profile.unlockedDangerLevel}</dd></div>
            <div><dt>${copy(locale, "Wins", "通关次数")}</dt><dd>${profile.victoriesByEgg[egg.id] ?? 0}</dd></div>
          </dl>
        </aside>
        <section class="setup-content">
          ${draft.step === "egg" ? eggStepMarkup(locale, profile, draft) : draft.step === "weapon" ? weaponStepMarkup(locale, profile, draft) : dangerStepMarkup(locale, profile, draft)}
        </section>
      </section>
    </main>`;
}

function eggStepMarkup(
  locale: Locale,
  profile: PlayerProfile,
  draft: DraftSelection,
): string {
  return `
    <div class="setup-heading"><p>01 / 03</p><h2>${copy(locale, "Choose your egg", "选择你的蛋")}</h2><span>${copy(locale, "Every egg changes the rules of the run.", "每颗蛋都会改变本局的构筑规则。")}</span></div>
    <div class="setup-card-grid egg-portrait-grid" data-egg-grid>
      ${EGGS.map((egg) => {
        const unlocked = profile.unlockedEggIds.includes(egg.id);
        const localized = localizeEgg(locale, egg);
        return `<article class="setup-choice egg-choice ${draft.eggId === egg.id ? "is-selected" : ""} ${unlocked ? "" : "is-locked"}" style="--choice-accent:${egg.accent}">
          ${spriteMarkup("egg", egg.id, "choice-egg")}
          <span>${localized.archetype}</span><strong>${localized.name}</strong>
          <button type="button" ${unlocked ? `data-setup-egg="${egg.id}"` : "disabled"} aria-label="${unlocked ? copy(locale, `Select ${localized.name}`, `选择${localized.name}`) : copy(locale, `${localized.name} locked`, `${localized.name}尚未解锁`)}"></button>
        </article>`;
      }).join("")}
    </div>`;
}

function weaponStepMarkup(
  locale: Locale,
  profile: PlayerProfile,
  draft: DraftSelection,
): string {
  const weapons = eligibleStartingWeapons(profile.unlockedWeaponIds, draft.eggId);
  return `
    <div class="setup-heading"><p>02 / 03</p><h2>${copy(locale, "Choose a starting weapon", "选择初始武器")}</h2><span>${copy(locale, "This is your first build decision, not a character default.", "这是本局第一次构筑选择，不再由角色直接绑定。")}</span></div>
    <div class="setup-card-grid weapon-choice-grid" data-weapon-grid>
      ${weapons.map((weapon) => {
        const localized = localizeWeapon(locale, weapon);
        return `<article class="setup-choice weapon-choice ${draft.weaponId === weapon.id ? "is-selected" : ""}" style="--choice-accent:${weapon.color}">
          ${spriteMarkup("weapon", weapon.id, "weapon-glyph")}
          <span>${weapon.family.toUpperCase()} · T1</span><strong>${localized.name}</strong>
          <p>${localized.description}</p>
          <dl><div><dt>DMG</dt><dd>${weapon.damage}</dd></div><div><dt>CD</dt><dd>${(weapon.intervalTicks / 60).toFixed(2)}s</dd></div><div><dt>RANGE</dt><dd>${weapon.projectileLifetimeTicks}</dd></div></dl>
          <button type="button" data-setup-weapon="${weapon.id}" aria-label="${copy(locale, `Select ${localized.name}`, `选择${localized.name}`)}"></button>
        </article>`;
      }).join("")}
    </div>
    <button class="setup-ghost-button setup-back" type="button" data-setup-back>${copy(locale, "Back to eggs", "返回角色选择")}</button>`;
}

function dangerStepMarkup(
  locale: Locale,
  profile: PlayerProfile,
  draft: DraftSelection,
): string {
  const descriptions = [
    copy(locale, "The complete base run.", "完整基础流程。"),
    copy(locale, "Enemies arrive sooner and hit harder.", "敌人更早出现，伤害提高。"),
    copy(locale, "Horde waves and elite threats join the run.", "加入群潮波和精英威胁。"),
    copy(locale, "Enemy health and damage scale sharply.", "敌人生命和伤害明显提高。"),
    copy(locale, "More elites, tighter arenas, fewer safe moments.", "精英更多，安全空间更少。"),
    copy(locale, "The full endgame pressure test.", "完整终局压力挑战。"),
  ];
  return `
    <div class="setup-heading"><p>03 / 03</p><h2>${copy(locale, "Choose danger", "选择危险等级")}</h2><span>${copy(locale, "Higher danger adds encounter rules, not only bigger numbers.", "更高危险会增加遭遇规则，而不只是堆高数值。")}</span></div>
    <div class="danger-grid" data-danger-grid>
      ${descriptions.map((description, level) => {
        const unlocked = level <= profile.unlockedDangerLevel;
        return `<button type="button" class="danger-card ${draft.dangerLevel === level ? "is-selected" : ""} ${unlocked ? "" : "is-locked"}" data-setup-danger="${level}" ${unlocked ? "" : "disabled"}><i>D${level}</i><strong>${copy(locale, `Danger ${level}`, `危险 ${level}`)}</strong><span>${unlocked ? description : copy(locale, "Win the previous danger to unlock", "通关前一危险等级后解锁")}</span></button>`;
      }).join("")}
    </div>
    <label class="endless-toggle"><input type="checkbox" data-setup-endless ${draft.endless ? "checked" : ""}/><i></i><span><strong>${copy(locale, "Endless mode", "无尽模式")}</strong><small>${copy(locale, "Continue beyond wave 20 until the shell breaks.", "第20波后继续战斗，直到蛋壳破碎。")}</small></span></label>
    <div class="setup-final-actions"><button class="setup-ghost-button" type="button" data-setup-back>${copy(locale, "Back to weapons", "返回武器选择")}</button><button class="primary" type="button" data-setup-start>${copy(locale, "Enter the arena", "进入战场")}</button></div>`;
}

function copy(locale: Locale, english: string, chinese: string): string {
  return locale === "zh-CN" ? chinese : english;
}
